/**
 * Session-wide record of the templates placed on each spread, for the gallery's "placed" highlight
 * (US-13): only the card of the most recently placed template that is still on the spread is
 * highlighted.
 *
 * A template becomes plain elements, so nothing in the document says which template they came
 * from; the session remembers it. Each placement is an instance (a template id plus the ids of the
 * elements it created), kept in placement order per spread. Which of them is "on the spread" is
 * derived from the ids of the elements that are currently there, so removing an instance's
 * elements, undoing its placement or redoing it needs no bookkeeping of its own: the highlight
 * falls back to the previous instance and comes back with a redo. Instances are never dropped
 * before logout, which is what lets a redo restore them. Resets on reload, as accepted for US-13.
 */
type PlacementInstance = {
  templateId: string
  elementIds: readonly string[]
}

const instancesBySpread = new Map<string, PlacementInstance[]>()
const liveIdsBySpread = new Map<string, ReadonlySet<string>>()

/** The spread whose highlight the gallery shows: the one most recently recorded or synced. */
let activeSpreadId: string | null = null

let placedTemplateId: string | null = null

const listeners = new Set<() => void>()

function resolvePlacedTemplateId(spreadId: string | null): string | null {
  if (!spreadId) return null

  const instances = instancesBySpread.get(spreadId)
  const liveIds = liveIdsBySpread.get(spreadId)

  if (!instances || !liveIds) return null

  for (let i = instances.length - 1; i >= 0; i--) {
    if (instances[i].elementIds.some((id) => liveIds.has(id))) return instances[i].templateId
  }

  return null
}

function refresh(): void {
  const next = resolvePlacedTemplateId(activeSpreadId)

  if (next === placedTemplateId) return

  placedTemplateId = next

  for (const listener of listeners) {
    listener()
  }
}

export function subscribeTemplatePlacement(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The template whose card is highlighted on the active spread, or null. */
export function getPlacedTemplateIdSnapshot(): string | null {
  return placedTemplateId
}

/** A template was just placed on `spreadId`; its elements are live from this moment. */
export function recordTemplatePlacement(
  spreadId: string,
  templateId: string,
  elementIds: readonly string[]
): void {
  const instances = instancesBySpread.get(spreadId) ?? []

  instances.push({ templateId, elementIds })
  instancesBySpread.set(spreadId, instances)

  const liveIds = new Set(liveIdsBySpread.get(spreadId))

  for (const id of elementIds) {
    liveIds.add(id)
  }

  liveIdsBySpread.set(spreadId, liveIds)
  activeSpreadId = spreadId
  refresh()
}

/** Tell the session which elements are on `spreadId` now — after any add, remove, undo or load. */
export function syncTemplatePlacements(
  spreadId: string,
  liveElementIds: ReadonlySet<string>
): void {
  liveIdsBySpread.set(spreadId, liveElementIds)
  activeSpreadId = spreadId
  refresh()
}

/** The spread on screen changed: the gallery now shows that spread's highlight. */
export function activateTemplateSpread(spreadId: string): void {
  activeSpreadId = spreadId
  refresh()
}

/** Called at logout: a new session starts with nothing placed. */
export function clearTemplatePlacements(): void {
  instancesBySpread.clear()
  liveIdsBySpread.clear()
  activeSpreadId = null
  refresh()
}
